import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { OveraLogger } from '@overa/shared';

import { ConsoleCapability } from '../console/console.model';
import { ConsoleService } from '../console/console.service';
import { PlatformCapabilityReport, PlatformWiringService } from './platform-wiring.service';

/**
 * Which screens each connected service of a platform actually serves, as the BFF probe reports it.
 *
 * Used to hide menu entries a platform cannot back: Harley's API has no app maintenance, and offering the
 * entry only leads to an error screen. The rule is **fail open**, like the launcher filter: the menu is a
 * convenience, not a door (the door is each service's API). So an entry is hidden only when the probe
 * positively says `Missing` for every destination of the platform; `Unknown`, an error, a 403 or no
 * report at all hide nothing.
 *
 * The probe endpoint needs the `platforms.view` console capability on that platform. Without it nothing is
 * asked and nothing is hidden, instead of firing a request that can only answer 403.
 */
@Injectable({ providedIn: 'root' })
export class PlatformCapabilitiesService {
  private readonly wiring = inject(PlatformWiringService);
  private readonly console = inject(ConsoleService);
  private readonly log = OveraLogger.instance.scope('PlatformCapabilities');

  /** Reports by platform code. Missing key = not asked yet. */
  private readonly reports = signal<ReadonlyMap<string, PlatformCapabilityReport[]>>(new Map());

  /** In-flight requests by platform code, so the guard and the overview share one call. */
  private readonly inFlight = new Map<string, Observable<PlatformCapabilityReport[]>>();

  /** The reports of a platform, or `null` if they have not been asked for (or could not be). */
  reportsFor(code: string): PlatformCapabilityReport[] | null {
    return this.reports().get(code) ?? null;
  }

  /**
   * Surfaces (`PlatformSurfaces` in the BFF) that no destination of the platform serves.
   *
   * Reads the signal, so a menu built inside an effect is rebuilt when the reports arrive.
   */
  missingSurfaces(code: string): ReadonlySet<string> {
    const reports = this.reports().get(code) ?? [];
    if (!reports.length) return new Set();

    const surfaces = Object.keys(reports[0].surfaces ?? {});
    return new Set(surfaces.filter((s) => reports.every((r) => r.surfaces?.[s] === 'Missing')));
  }

  /**
   * Asks the BFF for the platform's reports. Never errors: a failure resolves to `[]` and hides nothing.
   *
   * @param refresh Skips the BFF cache (10 min). What is needed right after deploying a destination.
   */
  load(code: string, refresh = false): Observable<PlatformCapabilityReport[]> {
    if (!refresh) {
      const known = this.reports().get(code);
      if (known) return of(known);

      const pending = this.inFlight.get(code);
      if (pending) return pending;
    }

    const request = this.console.load().pipe(
      switchMap(() =>
        this.console.canFor(ConsoleCapability.ViewPlatforms, code)
          ? this.wiring.capabilities(code, refresh).pipe(map((r) => r ?? []))
          : of([] as PlatformCapabilityReport[]),
      ),
      tap((r) => this.store(code, r)),
      catchError((error) => {
        this.log.warn(`Could not probe the capabilities of ${code}; nothing is hidden`, error);
        return of([] as PlatformCapabilityReport[]);
      }),
      finalize(() => this.inFlight.delete(code)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlight.set(code, request);
    return request;
  }

  private store(code: string, reports: PlatformCapabilityReport[]): void {
    const next = new Map(this.reports());
    next.set(code, reports);
    this.reports.set(next);
  }
}
